import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { X, Plus, Edit2, Trash2, DollarSign } from 'lucide-react';
import {
  fetchCreditsByOrder,
  createCredit,
  updateCredit,
  deleteCredit,
  selectAllCredits,
  selectCreditsLoading,
  selectCreditsError,
  clearError
} from '../../store/slices/creditsSlice';
import { calculateOrderItemTotal, formatPrice } from '../../utils/pricingCalculations';
import './CreditModal.css';

const CreditModal = ({ isOpen, onClose, order, productos = [] }) => {
  const dispatch = useDispatch();
  const credits = useSelector(selectAllCredits);
  const loading = useSelector(selectCreditsLoading);
  const error = useSelector(selectCreditsError);

  const [editingCreditId, setEditingCreditId] = useState(null);
  const [creditForm, setCreditForm] = useState({
    producto_id: '',
    cantidad_credito: '',
    motivo: ''
  });
  const [validationError, setValidationError] = useState('');

  // Load credits when modal opens
  useEffect(() => {
    if (isOpen && order?.id) {
      dispatch(fetchCreditsByOrder(order.id));
    }
  }, [isOpen, order?.id, dispatch]);

  // Clear error when modal closes
  useEffect(() => {
    if (!isOpen) {
      dispatch(clearError());
      setValidationError('');
      resetForm();
    }
  }, [isOpen, dispatch]);

  // Show error notification
  useEffect(() => {
    if (error) {
      setValidationError(error);
      setTimeout(() => {
        dispatch(clearError());
        setValidationError('');
      }, 5000);
    }
  }, [error, dispatch]);

  const resetForm = () => {
    setCreditForm({
      producto_id: '',
      cantidad_credito: '',
      motivo: ''
    });
    setEditingCreditId(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Get product details from order
  const getProductDetails = (productoId) => {
    const detalle = order?.detalles?.find(d => d.producto_id === parseInt(productoId));
    const producto = productos.find(p => p.id === parseInt(productoId));
    return {
      nombre: producto?.nombre || detalle?.producto?.nombre || 'Producto desconocido',
      cantidadPedida: detalle?.cantidad_cajas || detalle?.cantidad || 0
    };
  };

  // Calculate total credits for a product
  const getTotalCreditsForProduct = (productoId, excludeCreditId = null) => {
    return credits
      .filter(c => c.producto_id === parseInt(productoId) && c.id !== excludeCreditId)
      .reduce((sum, c) => sum + c.cantidad_credito, 0);
  };

  // Handle add/edit credit
  const handleStartEdit = (productoId, credit = null) => {
    if (credit) {
      // Edit existing credit
      setEditingCreditId(credit.id);
      setCreditForm({
        producto_id: credit.producto_id,
        cantidad_credito: credit.cantidad_credito,
        motivo: credit.motivo || ''
      });
    } else {
      // Add new credit
      setEditingCreditId(null);
      setCreditForm({
        producto_id: productoId,
        cantidad_credito: '',
        motivo: ''
      });
    }
    setValidationError('');
  };

  const handleSaveCredit = async () => {
    // Validation
    if (!creditForm.producto_id || !creditForm.cantidad_credito) {
      setValidationError('Debe ingresar la cantidad de crédito');
      return;
    }

    const cantidad = parseFloat(creditForm.cantidad_credito);
    if (cantidad <= 0) {
      setValidationError('La cantidad debe ser mayor a 0');
      return;
    }

    // Validate decimal precision (max 2 decimal places)
    const decimalRegex = /^\d+(\.\d{1,2})?$/;
    if (!decimalRegex.test(cantidad.toString())) {
      setValidationError('La cantidad debe tener máximo 2 decimales');
      return;
    }

    const { cantidadPedida } = getProductDetails(creditForm.producto_id);
    const totalExistentes = getTotalCreditsForProduct(creditForm.producto_id, editingCreditId);
    const totalCreditos = totalExistentes + cantidad;

    if (totalCreditos > cantidadPedida) {
      setValidationError(
        `La cantidad total de créditos (${totalCreditos}) no puede exceder la cantidad pedida (${cantidadPedida})`
      );
      return;
    }

    try {
      if (editingCreditId) {
        // Update existing credit
        await dispatch(updateCredit({
          id: editingCreditId,
          creditData: {
            cantidad_credito: cantidad,
            motivo: creditForm.motivo
          }
        })).unwrap();
      } else {
        // Create new credit
        await dispatch(createCredit({
          pedido_id: order.id,
          producto_id: parseInt(creditForm.producto_id),
          cantidad_credito: cantidad,
          motivo: creditForm.motivo
        })).unwrap();
      }
      resetForm();
    } catch (err) {
      // Error handled by Redux and displayed via useEffect
      console.error('Error saving credit:', err);
    }
  };

  const handleDeleteCredit = async (creditId) => {
    if (window.confirm('¿Está seguro de eliminar este crédito?')) {
      try {
        await dispatch(deleteCredit(creditId)).unwrap();
      } catch (err) {
        console.error('Error deleting credit:', err);
      }
    }
  };

  const handleCancelEdit = () => {
    resetForm();
    setValidationError('');
  };

  // Format credit quantity display (show decimals only when non-zero)
  const formatCreditQuantity = (cantidad) => {
    if (typeof cantidad !== 'number') {
      cantidad = parseFloat(cantidad);
    }
    if (isNaN(cantidad)) return '0';
    return cantidad.toLocaleString('es-CL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  };

  // Calculate credit value using the same algorithm as order pricing
  const calculateCreditValue = (credit) => {
    const producto = productos.find(p => p.id === credit.producto_id);
    if (!producto) return 0;
    
    const detalle = order?.detalles?.find(d => d.producto_id === credit.producto_id);
    if (!detalle) return 0;
    
    // Create a modified product object with the price from the order detail
    const productoConPrecio = {
      ...producto,
      precio_unitario: detalle.precio_unitario,
      precio_fob_mia: detalle.precio_unitario
    };
    
    try {
      // Use the same calculation as order items
      return calculateOrderItemTotal(productoConPrecio, order.tipo, credit.cantidad_credito);
    } catch (error) {
      console.error('Error calculating credit value:', error);
      return 0;
    }
  };

  // Calculate summary
  const totalCredito = credits.reduce((sum, c) => sum + calculateCreditValue(c), 0);
  const currency = order?.tipo === 'Exportación' ? 'USD' : 'CLP';
  
  const summary = {
    totalProductosConCredito: new Set(credits.map(c => c.producto_id)).size,
    totalCantidadCredito: credits.reduce((sum, c) => sum + c.cantidad_credito, 0),
    totalCredito: totalCredito,
    currency: currency,
    totalFormatted: formatPrice(totalCredito, currency)
  };

  if (!isOpen) return null;

  return (
    <div className="credit-modal-overlay" onClick={handleClose}>
      <div className="credit-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="credit-modal-header">
          <div className="credit-modal-title">
            <DollarSign size={24} />
            <div>
              <h2>Créditos del Pedido</h2>
              <p className="credit-modal-subtitle">
                {order?.clientes?.nombre} - Semana {order?.semana} / {order?.temporada}
              </p>
            </div>
          </div>
          <button className="credit-modal-close" onClick={handleClose}>
            <X size={24} />
          </button>
        </div>

        {/* Error message */}
        {validationError && (
          <div className="credit-error-message">
            {validationError}
          </div>
        )}

        {/* Content */}
        <div className="credit-modal-content">
          {loading && !credits.length ? (
            <div className="credit-loading">Cargando créditos...</div>
          ) : order?.detalles?.length === 0 ? (
            <div className="credit-empty">
              No hay productos en este pedido
            </div>
          ) : (
            <>
              {/* Products table */}
              <div className="credit-table-container">
                <table className="credit-table">
                  <thead>
                    <tr>
                      <th>Producto</th>
                      <th>Cantidad Pedida</th>
                      <th>Crédito Aplicado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order?.detalles?.map((detalle) => {
                      const producto = productos.find(p => p.id === detalle.producto_id);
                      const productCredits = credits.filter(c => c.producto_id === detalle.producto_id);
                      const totalCredito = productCredits.reduce((sum, c) => sum + c.cantidad_credito, 0);
                      const cantidadPedida = detalle.cantidad_cajas || detalle.cantidad || 0;

                      return (
                        <React.Fragment key={detalle.producto_id}>
                          <tr>
                            <td>{producto?.nombre || detalle?.producto?.nombre || 'Producto desconocido'}</td>
                            <td className="credit-table-number">{cantidadPedida}</td>
                            <td className="credit-table-number">
                              {totalCredito > 0 ? formatCreditQuantity(totalCredito) : '—'}
                            </td>
                            <td>
                              <button
                                className="credit-btn credit-btn-add"
                                onClick={() => handleStartEdit(detalle.producto_id)}
                                disabled={loading}
                              >
                                <Plus size={16} />
                                Agregar
                              </button>
                            </td>
                          </tr>

                          {/* Show existing credits for this product */}
                          {productCredits.map((credit) => (
                            <tr key={credit.id} className="credit-detail-row">
                              <td colSpan="2" className="credit-detail-motivo">
                                <span className="credit-detail-label">Motivo:</span> {credit.motivo || 'Sin motivo'}
                              </td>
                              <td className="credit-table-number">{formatCreditQuantity(credit.cantidad_credito)}</td>
                              <td>
                                <div className="credit-actions">
                                  <button
                                    className="credit-btn-icon"
                                    onClick={() => handleStartEdit(detalle.producto_id, credit)}
                                    disabled={loading}
                                    title="Editar"
                                  >
                                    <Edit2 size={16} />
                                  </button>
                                  <button
                                    className="credit-btn-icon credit-btn-delete"
                                    onClick={() => handleDeleteCredit(credit.id)}
                                    disabled={loading}
                                    title="Eliminar"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}

                          {/* Edit form for this product */}
                          {creditForm.producto_id === detalle.producto_id && (
                            <tr className="credit-edit-row">
                              <td colSpan="4">
                                <div className="credit-edit-form">
                                  <div className="credit-form-group">
                                    <label>Cantidad de Crédito *</label>
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0.01"
                                      value={creditForm.cantidad_credito}
                                      onChange={(e) => setCreditForm({
                                        ...creditForm,
                                        cantidad_credito: e.target.value
                                      })}
                                      placeholder="Cantidad"
                                      disabled={loading}
                                    />
                                  </div>
                                  <div className="credit-form-group credit-form-group-wide">
                                    <label>Motivo</label>
                                    <input
                                      type="text"
                                      value={creditForm.motivo}
                                      onChange={(e) => setCreditForm({
                                        ...creditForm,
                                        motivo: e.target.value
                                      })}
                                      placeholder="Ej: Merma en tránsito, problema de calidad..."
                                      disabled={loading}
                                    />
                                  </div>
                                  <div className="credit-form-actions">
                                    <button
                                      className="credit-btn credit-btn-save"
                                      onClick={handleSaveCredit}
                                      disabled={loading}
                                    >
                                      {editingCreditId ? 'Actualizar' : 'Guardar'}
                                    </button>
                                    <button
                                      className="credit-btn credit-btn-cancel"
                                      onClick={handleCancelEdit}
                                      disabled={loading}
                                    >
                                      Cancelar
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Summary */}
              <div className="credit-summary">
                <h3>Resumen de Créditos</h3>
                <div className="credit-summary-items">
                  <div className="credit-summary-item">
                    <span>Productos con crédito:</span>
                    <strong>{summary.totalProductosConCredito}</strong>
                  </div>
                  <div className="credit-summary-item">
                    <span>Total de cajas:</span>
                    <strong>{formatCreditQuantity(summary.totalCantidadCredito)}</strong>
                  </div>
                  <div className="credit-summary-item">
                    <span>Total en {summary.currency}:</span>
                    <strong>{summary.totalFormatted}</strong>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="credit-modal-footer">
          <button className="credit-btn credit-btn-close" onClick={handleClose}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreditModal;
